import { useReducedMotion } from '@/lib/useReducedMotion';
import { useEffect, useRef } from 'react';

const HOLD = 3000;

const play = (el: HTMLElement, name: string) => {
  const base = { fill: 'both' as const };
  if (name === 'פוקוס') {
    return el.animate([
      { filter: 'blur(0px)', opacity: 1 },
      { filter: 'blur(10px)', opacity: 0.25, offset: 0.45 },
      { filter: 'blur(0px)', opacity: 1 },
    ], { ...base, duration: 900 });
  }
  if (name === 'גליץ׳') {
    return el.animate([
      { transform: 'translateX(0)', filter: 'none' },
      { transform: 'translateX(-8px)', filter: 'hue-rotate(80deg)', offset: 0.25 },
      { transform: 'translateX(8px)', filter: 'hue-rotate(-40deg)', offset: 0.55 },
      { transform: 'translateX(0)', filter: 'none' },
    ], { ...base, duration: 520, easing: 'steps(4)' });
  }
  if (name === 'היפוך') {
    return el.animate([
      { transform: 'perspective(600px) rotateX(0deg)' },
      { transform: 'perspective(600px) rotateX(90deg)', offset: 0.45 },
      { transform: 'perspective(600px) rotateX(0deg)' },
    ], { ...base, duration: 800 });
  }
  if (name === 'סריקה') {
    return el.animate([
      { clipPath: 'inset(0 0 0 0)' },
      { clipPath: 'inset(0 100% 0 0)', offset: 0.45 },
      { clipPath: 'inset(0 0 0 0)' },
    ], { ...base, duration: 900, easing: 'cubic-bezier(.65,0,.35,1)' });
  }
  if (name === 'מתיחה') {
    return el.animate([
      { letterSpacing: '-0.04em', filter: 'blur(0px)' },
      { letterSpacing: '0.28em', filter: 'blur(2px)', offset: 0.5 },
      { letterSpacing: '-0.04em', filter: 'blur(0px)' },
    ], { ...base, duration: 900 });
  }
  if (name === 'טלוויזיה') {
    return el.animate([
      { transform: 'scaleY(1)', filter: 'brightness(1)' },
      { transform: 'scaleY(0.06)', filter: 'brightness(2.4)', offset: 0.45 },
      { transform: 'scaleY(1)', filter: 'brightness(1)' },
    ], { ...base, duration: 700 });
  }
  return el.animate([
    { opacity: 1, transform: 'translateY(0)' },
    { opacity: 0, transform: 'translateY(10px)', offset: 0.4 },
    { opacity: 1, transform: 'translateY(0)' },
  ], { ...base, duration: 700 });
};

const NAMES = ['פוקוס', 'גליץ׳', 'היפוך', 'סריקה', 'מתיחה', 'טלוויזיה', 'נשימה'];

/** The name stays put. Every three seconds one of seven transitions plays across it. */
export const WordPulse = ({ fontPx }: { fontPx: number }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced || !ref.current) return;
    let timer: ReturnType<typeof setTimeout>;
    let animation: Animation | undefined;
    let alive = true;
    let index = 0;
    const next = () => {
      timer = setTimeout(() => {
        if (!alive || !ref.current) return;
        animation = play(ref.current, NAMES[index++ % NAMES.length]);
        animation.finished.catch(() => undefined).then(() => { if (alive) next(); });
      }, HOLD);
    };
    next();
    return () => { alive = false; clearTimeout(timer); animation?.cancel(); };
  }, [reduced]);

  return (
    <span
      ref={ref}
      data-rd-source
      lang="en"
      dir="ltr"
      className="rd-display inline-block max-w-full font-semibold leading-none tracking-[-0.04em] text-[#EDEEF2]"
      style={{ fontSize: fontPx }}
    >
      nVision <span className="font-light text-[#8FA0C8]">Digital AI</span>
    </span>
  );
};
